import initSqlJs from "sql.js";
import { openDB } from "idb";

const DB_NAME = "datapilot_local_db";
const DB_STORE_SQLITE = "sqlite_file";
const DB_STORE_HISTORY = "query_history";

let SQL = null;
let db = null;

async function loadInitialSchema(databaseInstance) {
  try {
    const scriptTag = document.getElementById('base-schema-sql');
    if (!scriptTag) {
      throw new Error("base-schema-sql script tag not found in index.html");
    }
    const schemaSql = scriptTag.textContent;
    databaseInstance.exec(schemaSql);
    console.log("Initial schema loaded successfully from script tag.");
  } catch (error) {
    console.error("Error loading initial schema from script tag:", error);
    // Potentially re-throw or handle more gracefully
  }
}

export async function loadDatabase(forceNew = false, initialData = null) {
  if (!SQL) {
    SQL = await initSqlJs({
      locateFile: file => `https://sql.js.org/dist/${file}`
    });
  }

  // Open IndexedDB
  const idb = await openDB(DB_NAME, 3, { // Increment version to 3
    upgrade(db) {
      if (!db.objectStoreNames.contains(DB_STORE_SQLITE)) {
        db.createObjectStore(DB_STORE_SQLITE);
      }
      if (!db.objectStoreNames.contains(DB_STORE_HISTORY)) {
        db.createObjectStore(DB_STORE_HISTORY, { keyPath: 'id', autoIncrement: true });
      }
    },
  });

  if (forceNew) {
    db = new SQL.Database(); // Always create a fresh DB if forceNew is true
    await idb.clear(DB_STORE_SQLITE); // Clear existing data in IndexedDB
    await loadInitialSchema(db); // Load initial schema for fresh DB
  } else if (initialData) {
    db = new SQL.Database(initialData); // Load from provided initial data
  } else {
    const stored = await idb.get(DB_STORE_SQLITE, "db");
    if (stored) {
      db = new SQL.Database(new Uint8Array(stored));
    } else {
      db = new SQL.Database(); // fresh DB if no stored data
      await loadInitialSchema(db); // Load initial schema for fresh DB
    }
  }

  return db;
}

export async function saveDatabase() {
  const binary = db.export();
  const idb = await openDB(DB_NAME, 3); // Ensure version 3
  await idb.put(DB_STORE_SQLITE, binary, "db");
}

export async function executeQuery(query) {
  let success = false;
  let result = null;
  let error = null;
  let executionTime = 0;
  let affectedRows = 0; // Initialize affectedRows

  try {
    const start = performance.now();
    result = db.exec(query);
    affectedRows = db.getRowsModified(); // Get affected rows for DML statements
    await saveDatabase(); // persist changes
    const end = performance.now();
    success = true;
    executionTime = (end - start).toFixed(2);
    console.log("SQL execution successful. Result:", result, "Affected Rows:", affectedRows);
    // The history saving logic is now outside the try-catch to ensure it always runs
  } catch (err) {
    error = err.message;
    console.error("SQL execution error:", err.message);
  }

  // Save query to history (moved outside try-catch to ensure it always runs)
  const idb = await openDB(DB_NAME, 3); // Ensure version 3
  const historyItem = {
    query,
    executed_at: new Date().toISOString(),
    success,
    error_message: error,
    execution_time_ms: parseFloat(executionTime),
  };
  await idb.add(DB_STORE_HISTORY, historyItem);
  console.log("History item added to IndexedDB:", historyItem);

  return { success, result, error, executionTime, affectedRows };
}

export async function loadHistoryFromIndexedDB() {
  const idb = await openDB(DB_NAME, 3); // Ensure version 3
  const history = await idb.getAll(DB_STORE_HISTORY);
  console.log("History loaded from IndexedDB:", history);
  return history;
}

export async function clearHistoryInIndexedDB() {
  const idb = await openDB(DB_NAME, 3); // Ensure version 3
  await idb.clear(DB_STORE_HISTORY);
  console.log("History cleared from IndexedDB.");
}
